// Deterministic PRNG (sfc32) with stateless stream derivation.
//
// The engine never serializes RNG state: every consumer derives a fresh stream
// from the game's master seed plus a label path, e.g. rngFor(seed, turn, 'combat',
// battleId). Identical labels yield identical streams on every peer, so replays
// and live runs agree bit-for-bit. Uses only u32 integer ops.

import { xxhash32 } from './hash';

const TE = new TextEncoder();

/** 128-bit master seed as 32 lowercase hex chars. Generated outside the engine. */
export type MasterSeed = string;

export function isValidMasterSeed(seed: string): boolean {
  return /^[0-9a-f]{32}$/.test(seed);
}

export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  constructor(a: number, b: number, c: number, d: number) {
    this.a = a >>> 0;
    this.b = b >>> 0;
    this.c = c >>> 0;
    this.d = d >>> 0;
    // Warm up: sfc32 needs a few rounds to decorrelate weak seeds.
    for (let i = 0; i < 12; i++) this.nextU32();
  }

  /** Next value in [0, 2^32). */
  nextU32(): number {
    const t = (((this.a + this.b) | 0) + this.d) | 0;
    this.d = (this.d + 1) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.c = (this.c + t) | 0;
    return t >>> 0;
  }

  /** Uniform integer in [0, n) without modulo bias (rejection sampling). */
  int(n: number): number {
    if (!Number.isSafeInteger(n) || n <= 0 || n > 0x100000000) {
      throw new Error(`Rng.int: bad bound ${n}`);
    }
    if (n === 1) return 0;
    const limit = 0x100000000 - (0x100000000 % n);
    for (;;) {
      const v = this.nextU32();
      if (v < limit) return v % n;
    }
  }

  /** Uniform integer in [min, max] inclusive. */
  range(min: number, max: number): number {
    if (max < min) throw new Error(`Rng.range: ${min} > ${max}`);
    return min + this.int(max - min + 1);
  }

  /** True with probability pct/100 (integer percent). */
  chancePct(pct: number): boolean {
    if (!Number.isSafeInteger(pct)) throw new Error(`Rng.chancePct: ${pct}`);
    if (pct <= 0) return false;
    if (pct >= 100) return true;
    return this.int(100) < pct;
  }

  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error('Rng.pick: empty array');
    return arr[this.int(arr.length)]!;
  }

  /** In-place Fisher-Yates shuffle. */
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const tmp = arr[i]!;
      arr[i] = arr[j]!;
      arr[j] = tmp;
    }
    return arr;
  }
}

/**
 * Derive a stream from the master seed and a label path. Labels may be strings
 * or safe integers; the same path always produces the same stream.
 */
export function rngFor(seed: MasterSeed, ...labels: Array<string | number>[] | Array<string | number>): Rng {
  if (!isValidMasterSeed(seed)) throw new Error(`invalid master seed: ${seed}`);
  const flat = (labels as Array<string | number>).map((l) => String(l)).join(' ');
  const labelBytes = TE.encode(flat);
  const s0 = parseInt(seed.slice(0, 8), 16);
  const s1 = parseInt(seed.slice(8, 16), 16);
  const s2 = parseInt(seed.slice(16, 24), 16);
  const s3 = parseInt(seed.slice(24, 32), 16);
  const h0 = xxhash32(labelBytes, s0);
  const h1 = xxhash32(labelBytes, s1 ^ 0x9e3779b9);
  const h2 = xxhash32(labelBytes, s2 ^ 0x85ebca6b);
  const h3 = xxhash32(labelBytes, s3 ^ 0xc2b2ae35);
  return new Rng(h0, h1, h2, h3);
}
